<template>

  <v-layout>
    <v-dialog v-model="showExportDefaults"
              persistent
              max-width="500">
      <v-card>
        <v-stepper v-model="exportDefaultsStep">
          <v-stepper-header>
            <v-stepper-step step="1" :completed="exportDefaultsStep > 1">Invoice Defaults</v-stepper-step>
            <v-stepper-step step="2" :completed="exportDefaultsStep > 2">Expense Defaults</v-stepper-step>
            <v-stepper-step step="3" :completed="exportDefaultsStep > 3" v-if="configuredToCollectTax">Tax</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="1">
              <h3>Direct Invoicing</h3>
              <span class="font-weight-light">
                  When you cut an invoice directly to a customer or dispatcher
              </span>

              <v-list>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title class="font-weight-bold">Invoice Sales Item</v-list-tile-title>
                    <v-list-tile-sub-title>
                      transportation sales line item for newly created invoices
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile class="pl-3 pt-1">
                  <v-autocomplete
                    v-if="editExportDefaults"
                    class="mt-2"
                    :items="salesItems"
                    :loading="loadingSalesItems"
                    :disabled="loadingSalesItems"
                    label="Quickbooks Sales Item"
                    item-text="description"
                    item-value="itemId"
                    v-model="editExportDefaults.invoices.salesItem"
                    return-object>
                  </v-autocomplete>
                  <v-list-tile-avatar class="pl-3">
                    <v-btn fab small flat color="accent" :disabled="loadingSalesItems">
                      <v-icon color="accent" @click="refreshQuickbooksSalesItems(company.id)">refresh</v-icon>
                    </v-btn>
                  </v-list-tile-avatar>
                </v-list-tile>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title class="font-weight-bold">
                      Customer Payment Terms
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      payment terms from customers for newly created invoices
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile class="pl-3">
                  <v-select
                    v-if="editExportDefaults"
                    :loading="loadingSalesTerms"
                    :disabled="loadingSalesTerms"
                    :items="salesTerms"
                    label="Payment Terms"
                    item-text="name"
                    item-value="id"
                    v-model="editExportDefaults.invoices.paymentTerms"
                    return-object>
                  </v-select>
                  <v-list-tile-avatar class="pl-3">
                    <v-btn fab small flat color="accent" :disabled="loadingSalesTerms">
                      <v-icon color="accent" @click="refreshQuickbooksPaymentTerms(company.id)">
                        refresh
                      </v-icon>
                    </v-btn>
                  </v-list-tile-avatar>
                </v-list-tile>
              </v-list>

              <v-divider/>
              <v-btn block large
                     :loading="loadingBtn"
                     @click="exportDefaultsStep = 2"
                     color="accent">
                <v-icon large color="white" class="pr-2">
                  <template>
                    credit_card
                  </template>
                </v-icon>
                <span>{{ $t( 'terms.billable-expense' ) }} Defaults</span>
              </v-btn>

              <v-btn block flat
                     :loading="loadingBtn"
                     color="accent" @click="showExportDefaults = false">
                <v-icon class="pr-2">cancel</v-icon>
                Cancel
              </v-btn>
            </v-stepper-content>

            <v-stepper-content step="2">
              <h3>{{ $t( 'terms.billable-expense' ) }}</h3>
              <span class="font-weight-light">
                  When a {{ $t( 'terms.vendor' ) }} handles your customer's load
              </span>

              <v-list>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title class="font-weight-bold">Expense Account</v-list-tile-title>
                    <v-list-tile-sub-title>
                      account based expense line for newly created bills
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile class="pl-3 pt-1 pb-3">
                  <v-autocomplete
                    v-if="editExportDefaults"
                    class="mt-2"
                    :items="quickbooksExpenseAccounts"
                    :loading="loadingAccounts"
                    :disabled="loadingAccounts"
                    label="Quickbooks Expense Account"
                    item-text="accountName"
                    item-value="accountId"
                    v-model="editExportDefaults.bills.expenseAccount"
                    return-object>
                  </v-autocomplete>
                  <v-list-tile-avatar class="pl-3">
                    <v-btn fab small flat color="accent" :disabled="loadingAccounts">
                      <v-icon color="accent" @click="refreshQuickbooksAccounts(company.id)">refresh</v-icon>
                    </v-btn>
                  </v-list-tile-avatar>
                </v-list-tile>

                <v-divider/>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title class="font-weight-bold tall-title">
                      {{ $t( 'terms.vendor' ) }} Payment Terms
                      <help-popover>
                        <p class="ma-0 font-weight-light">
                          Vendor discounts are applied unique to the vendor. For new vendors, {{ $t('site-title.brief') }} will
                          suggest the following terms and accounts.
                        </p>
                      </help-popover>
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      payment terms to vendors for newly created bills
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile class="pl-3">
                  <v-select
                    v-if="editExportDefaults"
                    :items="salesTerms"
                    :loading="loadingSalesTerms"
                    :disabled="loadingSalesTerms"
                    label="Payment Terms"
                    item-text="name"
                    item-value="id"
                    v-model="editExportDefaults.bills.paymentTerms"
                    return-object>
                  </v-select>
                  <v-list-tile-avatar class="pl-3">
                    <v-btn fab small flat color="accent" :disabled="loadingSalesTerms">
                      <v-icon color="accent" @click="refreshQuickbooksPaymentTerms(company.id)">
                        refresh
                      </v-icon>
                    </v-btn>
                  </v-list-tile-avatar>
                </v-list-tile>
              </v-list>

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title class="font-weight-bold">Dispatch Income</v-list-tile-title>
                  <v-list-tile-sub-title>
                    account based dispatch fee collected for newly created bills
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile class="pl-3 pt-1 pb-3">
                <v-autocomplete
                  v-if="editExportDefaults"
                  class="mt-2"
                  :items="quickbooksIncomeAccounts"
                  :loading="loadingAccounts"
                  :disabled="loadingAccounts"
                  label="Quickbooks Dispatch Fee Account"
                  item-text="accountName"
                  item-value="accountId"
                  v-model="editExportDefaults.bills.dispatchFeeAccount"
                  return-object>
                </v-autocomplete>
                <v-list-tile-avatar class="pl-3" :disabled="loadingAccounts">
                  <v-btn fab small flat color="accent" :disabled="loadingAccounts">
                    <v-icon color="accent" @click="refreshQuickbooksAccounts(company.id)">refresh</v-icon>
                  </v-btn>
                </v-list-tile-avatar>
              </v-list-tile>

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title class="font-weight-bold tall-title">
                    Trailer Lease Income
                    <help-popover>
                      <p class="ma-0 font-weight-light">
                        Trailer lease fees are only suggested for drivers who are part of your company's
                        preferred team of drivers.  You can of course apply any income discount to any vendor.
                      </p>
                    </help-popover>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    account based trailer lease fee collected for newly created bills
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile class="pl-3 pt-1 pb-3">
                <v-autocomplete
                  v-if="editExportDefaults"
                  class="mt-2"
                  :items="quickbooksIncomeAccounts"
                  :loading="loadingAccounts"
                  :disabled="loadingAccounts"
                  label="Quickbooks Trailer Lease Fee Account"
                  item-text="accountName"
                  item-value="accountId"
                  v-model="editExportDefaults.bills.trailerLeaseFeeAccount"
                  return-object>
                </v-autocomplete>
                <v-list-tile-avatar class="pl-3">
                  <v-btn fab small flat color="accent" :disabled="loadingAccounts">
                    <v-icon color="accent" @click="refreshQuickbooksAccounts(company.id)">refresh</v-icon>
                  </v-btn>
                </v-list-tile-avatar>
              </v-list-tile>

              <v-divider/>
              <v-btn block large
                     :loading="loadingBtn"
                     @click="completeStep2()"
                     color="accent">
                <v-icon large color="white" class="pr-2" v-if="configuredToCollectTax">
                  account_balance
                </v-icon>
                <span v-if="configuredToCollectTax">Tax Liability</span>
                <v-icon large color="white" class="pr-2" v-if="!configuredToCollectTax">
                  save
                </v-icon>
                <span v-if="!configuredToCollectTax">Save Quickbooks Export Defaults</span>
              </v-btn>
              <v-btn block
                     :loading="loadingBtn"
                     @click="exportDefaultsStep = 1"
                     color="accent">
                <v-icon large color="white" class="pr-2">
                  receipt
                </v-icon>
                <span>Invoicing Defaults</span>
              </v-btn>

              <v-btn block flat
                     :loading="loadingBtn"
                     color="accent" @click="showExportDefaults = false">
                <v-icon class="pr-2">cancel</v-icon>
                Cancel
              </v-btn>
            </v-stepper-content>

            <v-stepper-content step="3" v-if="configuredToCollectTax">
              <h3>Tax Liability</h3>
              <span class="font-weight-light">
                  When you collect or pay transaction taxes, add these Tax Codes to line items
              </span>

              <v-list two-line>
                <v-list-tile>
                  <v-autocomplete
                    v-if="editExportDefaults"
                    class="mt-2"
                    :items="quickbooksSalesTaxCodes(company.id)"
                    :loading="loadingTaxCodes"
                    :disabled="!configuredToCollectTax || loadingTaxCodes"
                    label="Quickbooks Sales Tax Code"
                    item-text="name"
                    item-value="id"
                    v-model="editExportDefaults.invoices.taxCode"
                    :return-object="true"
                    :error-messages="errors.first('salesTaxCode', 'qbTaxCode')"
                    data-vv-as="Quickbooks Sales Tax Code"
                    data-vv-name="salesTaxCode"
                    data-vv-scope="qbTaxCode"
                    v-validate="'required'"
                  >
                  </v-autocomplete>

                  <v-list-tile-avatar class="pl-3">
                    <v-btn fab small flat color="accent" :disabled="!configuredToCollectTax || loadingTaxCodes" v-if="editExportDefaults">
                      <v-icon color="accent" @click="refreshQuickbooksTaxCodes(company.id)">refresh</v-icon>
                    </v-btn>
                  </v-list-tile-avatar>
                </v-list-tile>
                <v-list-tile>
                  <v-autocomplete
                    v-if="editExportDefaults"
                    class="mt-2"
                    :items="quickbooksPurchaseTaxCodes(company.id)"
                    v-model="editExportDefaults.bills.taxCode"
                    :loading="loadingTaxCodes"
                    :disabled="!configuredToCollectTax || loadingTaxCodes"
                    label="Quickbooks Purchase Tax Code"
                    item-text="name"
                    item-value="id"
                    :return-object="true"
                    :error-messages="errors.first('purchaseTaxCode', 'qbTaxCode')"
                    data-vv-as="Quickbooks Purchase Tax Code"
                    data-vv-name="purchaseTaxCode"
                    data-vv-scope="qbTaxCode"
                    v-validate="'required'"
                  >
                  </v-autocomplete>

                  <v-list-tile-avatar class="pl-3">
                    <v-btn fab small flat color="accent" :disabled="!configuredToCollectTax || loadingTaxCodes" v-if="editExportDefaults">
                      <v-icon color="accent" @click="refreshQuickbooksTaxCodes(company.id)">refresh</v-icon>
                    </v-btn>
                  </v-list-tile-avatar>
                </v-list-tile>
              </v-list>

              <v-divider/>
              <v-btn block large
                     :loading="loadingBtn"
                     @click="saveExportDefaults"
                     color="accent">
                <v-icon large color="white" class="pr-2">
                  <template>
                    save
                  </template>
                </v-icon>
                <span>Save Quickbooks Export Defaults</span>
              </v-btn>
              <v-btn block
                     :loading="loadingBtn"
                     @click="exportDefaultsStep = 2"
                     color="accent">
                <v-icon large color="white" class="pr-2">
                  credit_card
                </v-icon>
                <span>{{ $t( 'terms.billable-expense' ) }} Defaults</span>
              </v-btn>

              <v-btn block flat
                     :loading="loadingBtn"
                     color="accent" @click="showExportDefaults = false">
                <v-icon class="pr-2">cancel</v-icon>
                Cancel
              </v-btn>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

      </v-card>
    </v-dialog>

    <v-dialog v-if="vendorDispatcher"
              v-model="showUpdateVendorDispatcher"
              persistent
              max-width="500">
      <v-card :class="{'pa-4': !$vuetify.breakpoint.smAndDown}">
        <v-layout column>
          <v-toolbar extended dense>
            <v-icon color="accent">headset_mic</v-icon>
            <v-toolbar-title>{{ vendorDispatcher.companyName }}</v-toolbar-title>
            <v-list slot="extension">
              <v-list-tile class="pl-2">
                <v-list-tile-content>
                  <v-list-tile-title v-if="false">A little more prominent</v-list-tile-title>
                  <v-list-tile-sub-title class="font-italic">Dispatcher Contact Info</v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-toolbar>
          <v-spacer class="mb-3" />

          <v-card pa-0 flat>
            <v-text-field v-model="vendorDispatcher.name" label="Name"></v-text-field>
            <v-text-field v-model="vendorDispatcher.email" label="Email"></v-text-field>
            <v-text-field v-model="vendorDispatcher.phone" label="Phone (for SMS Texts)"></v-text-field>
          </v-card>

          <v-card-actions class="align-right">
            <v-btn color="accent"
                   :loading="loadingBtn"
                   flat large outline
                   @click="closeUpdateVendorDispatcherDialog">
              <v-icon large class="pr-2">cancel</v-icon>
              Cancel
            </v-btn>
            <v-btn color="accent"
                   :loading="loadingBtn"
                   large
                   @click="saveVendorDispatcher">
              <v-icon large class="pr-2">save</v-icon>
              Save
            </v-btn>
          </v-card-actions>
        </v-layout>
      </v-card>
    </v-dialog>

    <v-container fluid class="pa-0 ma-0">
      <v-layout column class="pa-0">
        <v-flex xs12>
          <v-card expand flat class=" full-card-desktop py-0 px-0"   >
            <v-tabs hide-slider class="qb_tabs">
              <v-tabs-items v-model="activeTab">
                <v-tab-item value="connect" lazy>

                    <v-toolbar flat color="white" class="darken-1">
                      <v-icon color="red" v-if="!hasCurrentCredential">
                        report
                      </v-icon>
                      <v-icon color="green" v-else>offline_bolt</v-icon>
                      <v-toolbar-title>Quickbooks Connect</v-toolbar-title>

                      <v-spacer/>
                      <v-toolbar-items>

                        <v-btn small flat color="accent" @click="refreshQuickbooksAccountInfo">
                          <v-layout column class="align-center">
                            <v-icon>download</v-icon>
                            Refresh
                          </v-layout>
                        </v-btn>
                      </v-toolbar-items>
                    </v-toolbar>
                    <template v-if="!hasCurrentCredential || daysUntilQuickbooksCredentialsExpire(company) < 8">
                      <v-alert
                        :value="!hasCurrentCredential"
                        type="error"
                      >
                        <strong>Requires Authentication</strong>
                      </v-alert>
                      <v-alert type="warning"
                               :value="hasCurrentCredential && daysUntilQuickbooksCredentialsExpire(company) < 8">
                        <strong>Credentials expire in {{ daysUntilQuickbooksCredentialsExpire(company) }} days</strong>
                      </v-alert>
                      <div class="pa-2">
                        <p>For your security, Quickbooks requires that you provide Quickbooks credentials at
                          least every 100 days.</p>
                        <p>Click below to continue allowing {{ $t('site-title.brief') }} to communicate with your
                          Quickbooks account.</p>
                      </div>
                    </template>

                    <v-list two-lines>
                      <v-subheader>
                        <v-icon class="pr-2">settings</v-icon>
                        Quickbooks Company Profile
                      </v-subheader>

                      <v-list-tile>
                        <v-list-tile-action>
                          <v-icon color="primary">
                            business
                          </v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>{{ quickbooksCompanyName(company) }}</v-list-tile-title>
                          <v-list-tile-sub-title>{{ quickbooksCompanyLegalName(company) }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-action>
                          <v-icon color="primary">
                            repeat
                          </v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>{{ quickbooksSubscriptionSkuName(company) }}</v-list-tile-title>
                          <v-list-tile-sub-title v-if="!subscriptionSupportsBilling">{{ quickbooksSubscriptionRequiredForBilling }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                        <v-list-tile-avatar v-if="!subscriptionSupportsBilling">
                          <v-btn fab flat small color="red">
                            <v-icon class="pr-2">report</v-icon>
                          </v-btn>
                        </v-list-tile-avatar>
                      </v-list-tile>
                      <v-list-tile v-if="configuredToCollectTax">
                        <v-list-tile-action>
                          <v-icon color="primary">
                            account_balance
                          </v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>Default Tax Codes Applied</v-list-tile-title>
                          <v-list-tile-sub-title>to invoice and bill transactions. Can be modified per load if necessary.</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile v-else>
                        <v-list-tile-action>
                          <v-icon color="primary" disabled>
                            account_balance
                          </v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>Tax Codes do not Apply</v-list-tile-title>
                          <v-list-tile-sub-title>Your Quickbooks Account is not set up to remit transaction tax. To change this, Go to GST and select Get started.</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>

                      <v-subheader>
                        <v-icon class="pr-2">fingerprint</v-icon>
                        Authorization
                      </v-subheader>

                      <v-list-tile>
                        <v-list-tile-action>
                          <v-icon color="primary">
                            person_outline
                          </v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            {{ quickbooksCompanyAuthenticatedBy(company)}}
                          </v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span class="font-italic">{{ quickbooksCompanyAuthenticated(company) | formatDate }}</span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-action>
                          <v-icon color="primary">event</v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>{{ quickbooksCompanyCredentialExpires(company) | formatDay }}</v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span class="font-italic">Must re-authorize {{ $t('site-title.brief') }} on or before</span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile v-if="hasOfficePermission">
                        <v-list-tile-action>
                          <v-icon color="primary">autorenew</v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            Authorize {{ $t('site-title.brief') }}
                          </v-list-tile-title>
                        </v-list-tile-content>

                        <v-spacer/>
                        <v-quickbooks-connect-btn :company-id="company.id"
                                                  @connecting="connectAccount"
                                                  @connected="connectCompleted"
                                                  :loading="qbButtonLoading"/>
                      </v-list-tile>
                      <v-list-tile v-if="hasOfficePermission && isConnected">
                        <v-list-tile-action>
                          <v-icon color="red">clear</v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            Revoke Permission
                          </v-list-tile-title>
                        </v-list-tile-content>

                        <v-spacer/>
                        <v-btn flat class="pl-0 pr-0 mr-2" color="red"
                               @click="disconnectAccount">
                          <v-icon class="pr-2">cancel</v-icon>
                          Revoke
                        </v-btn>
                      </v-list-tile>
                    </v-list>

                </v-tab-item>
                <v-tab-item value="defaults" lazy>
                  <v-toolbar flat color="white" class="darken-1">
<!--                    <v-icon color="green">import_export</v-icon>-->
                    <img class="align-center cursor-pointer"
                         height="38" width="38"
                         src="/static/Quickbooks-Logo.png"/>
                    <v-toolbar-title>Quickbooks Export Defaults</v-toolbar-title>

                    <v-spacer/>
                    <v-toolbar-items>
                      <v-btn small flat color="accent" @click="showExportDefaultsDialog">
                        <v-layout column class="align-center">
                          <v-icon>edit</v-icon>
                            Edit
                        </v-layout>
                      </v-btn>
                      <v-btn small flat color="accent" @click="reloadCompanyQuickbooksData">
                        <v-layout column class="align-center">
                          <v-icon>refresh</v-icon>
                            Reload
                        </v-layout>
                      </v-btn>
                    </v-toolbar-items>
                  </v-toolbar>

                  <v-list three-lines>

                    <v-subheader>
                      <v-icon class="pr-2">receipt</v-icon>
                      Direct Invoicing
                    </v-subheader>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Invoice Sales Item</v-list-tile-title>
                        <v-list-tile-sub-title v-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>{{ defaultInvoiceSalesItem.description }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Customer Payment Terms</v-list-tile-title>
                        <v-list-tile-sub-title v-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>{{ defaultCustomerInvoiceTerms.name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-subheader>
                      <v-icon class="pr-2">credit_card</v-icon>
                      {{ $t( 'terms.billable-expense' ) }}
                    </v-subheader>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Transportation Fee Expense Account</v-list-tile-title>
                        <v-list-tile-sub-title v-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>{{ defaultTransportationFeeExpenseAccount.accountName }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ $t( 'terms.vendor' ) }} Payment Terms</v-list-tile-title>
                        <v-list-tile-sub-title v-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>{{ defaultVendorBillTerms.name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Dispatch Fee Income Account</v-list-tile-title>
                        <v-list-tile-sub-title v-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>
                          {{ defaultDispatchFeeIncomeAccount.accountName }}
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Trailer Lease Fee Account</v-list-tile-title>
                        <v-list-tile-sub-title v-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>
                          {{ defaultTrailerLeaseFeeIncomeAccount.accountName }}
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>


                    <v-subheader v-if="configuredToCollectTax">
                      <v-icon class="pr-2">account_balance</v-icon>
                      Tax Liability
                    </v-subheader>

                    <v-list-tile v-if="configuredToCollectTax" class="pb-2">
                      <v-list-tile-content>
                        <v-list-tile-title>Sales Tax Code</v-list-tile-title>
                        <v-list-tile-sub-title v-if="defaultTaxCodes.sales.id">{{ defaultTaxCodes.sales.name }} ({{ defaultTaxCodes.sales.description }})</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>None Selected.</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-list-tile v-if="configuredToCollectTax" class="pb-2">
                      <v-list-tile-content>
                        <v-list-tile-title>Purchase Tax Code</v-list-tile-title>
                        <v-list-tile-sub-title v-if="defaultTaxCodes.purchase.id">{{ defaultTaxCodes.purchase.name }} ({{ defaultTaxCodes.purchase.description }})</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else-if="taxCodesStatus[company.id]">loading ...</v-list-tile-sub-title>
                        <v-list-tile-sub-title v-else>None Selected.</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-tab-item>
                <v-tab-item value="customers" lazy v-if="isConnected">
                  <v-toolbar flat color="white" class="darken-1">
                    <v-icon color="green">face</v-icon>
                    <v-toolbar-title>
                      Quickbooks Customers using {{ $t('site-title.brief') }}

                      <v-icon class="mr-3" @click="refreshCustomers()">refresh</v-icon>
                    </v-toolbar-title>

                    <v-spacer/>
                    <v-text-field
                      class="mx-3"
                      v-model="customerSearch"
                      flat
                      label="Search"
                      @click:append="customerSearch = null"
                      append-icon="close"
                      prepend-inner-icon="search"
                    ></v-text-field>
                  </v-toolbar>

                  <v-list>
                    <v-subheader>Customers with {{ $t('site-title.brief') }} Accounts</v-subheader>
                    <v-list-tile v-if="loadingCustomers">
                      <v-progress-circular
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                    </v-list-tile>
                    <template v-for="(customer, i) in knownCustomers" v-if="!loadingCustomers">
                      <v-list-tile :key="'customer-no-discounts' + i">
                        <v-list-tile-action>
                          <v-btn flat fab small color="accent"
                                 @click="removeCustomerMapping(customer)">
                            <v-icon>cancel</v-icon>
                          </v-btn>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>{{ customer.ltnCompanyName }}</v-list-tile-title>
                          <v-list-tile-sub-title>
                            {{ customer.qbDisplayName }} ({{ customer.qbCompanyId }})
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </template>
                    <v-subheader>Other Quickbooks Customers</v-subheader>
                    <v-list-group>
                      <v-list-tile slot="activator">
                        <v-list-tile-action>
                          <img class="align-center cursor-pointer"
                               height="32" width="32"
                               src="/static/Quickbooks-Logo.png"/>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>Quickbooks Customers</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <template v-for="(customer, i) in unknownCustomers"  v-if="!loadingCustomers">
                        <v-list-tile :key="'unknown-customer-' + i">
                          <v-list-tile-action />
                          <v-list-tile-content>
                            <v-list-tile-title>{{ customer.qbDisplayName }}</v-list-tile-title>
                          </v-list-tile-content>
<!--                          <v-list-tile-action>-->
<!--                            <v-btn flat small-->
<!--                                   @click="$confirm('Make this work. Invite your buddy')"-->
<!--                                   color="accent">-->
<!--                              <v-icon class="pr-2">person_add</v-icon>-->
<!--                              Invite to LTN-->
<!--                            </v-btn>-->
<!--                          </v-list-tile-action>-->
                        </v-list-tile>
                      </template>
                    </v-list-group>
                  </v-list>
                </v-tab-item>
                <v-tab-item value="qb_vendors" lazy>
                  <v-toolbar flat color="white" class="darken-1">
                    <v-icon color="green">share</v-icon>
                    <v-toolbar-title>
                      Quickbooks Vendors

                      <v-icon class="mr-3" @click="refreshVendors()">refresh</v-icon>
                    </v-toolbar-title>

                    <v-spacer/>
                    <v-text-field
                      class="mx-3"
                      v-model="vendorSearch"
                      flat
                      label="Search"
                      @click:append="vendorSearch = null"
                      append-icon="close"
                      prepend-inner-icon="search"
                    ></v-text-field>
                  </v-toolbar>

                  <v-list three-line="">
                    <v-subheader>Vendors with Discounts</v-subheader>
                    <v-list-tile v-if="loadingVendors">
                      <v-progress-circular
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                    </v-list-tile>
                    <template v-else
                              v-for="(vendor, i) in knownVendorsWithDiscounts">
                      <v-list-group :key="'vendor-with-discounts-' + i">
                        <v-list-tile :key="'vendor-' + i"
                                     slot="activator">
                          <v-list-tile-action>
                            <v-icon color="accent">edit</v-icon>
                          </v-list-tile-action>
                          <v-list-tile-content>
                            <v-list-tile-title>{{ vendor.ltnCompanyName || vendor.ltnEmployeeName || vendor.qbDisplayName }}</v-list-tile-title>
                            <v-list-tile-sub-title>{{ vendor.qbDisplayName }} ({{ vendor.qbCompanyId }})</v-list-tile-sub-title>
                            <v-list-tile-sub-title>{{ vendorSalesTerms(vendor).name }}</v-list-tile-sub-title>
                          </v-list-tile-content>
                        </v-list-tile>
                        <v-list-tile v-if="isDirectDispatched(vendor)">
                          <v-list-tile-action>
                            <v-btn flat fab small color="accent" @click="showUpdateVendorDispatcherDialog(vendor)">
                              <v-icon>headset_mic</v-icon>
                            </v-btn>
                          </v-list-tile-action>
                          <v-list-tile-content>
                            <v-list-tile-title v-if="vendor.dispatcher">Dispatcher {{ vendor.dispatcher.name }}</v-list-tile-title>
                            <v-list-tile-title v-else>Add dispatcher contact info</v-list-tile-title>
                            <v-list-tile-sub-title class="font-italic">{{ _.get(vendor, 'dispatcher.email', '') }}</v-list-tile-sub-title>
                            <v-list-tile-sub-title class="font-italic">{{ _.get(vendor, 'dispatcher.phone', '') }}</v-list-tile-sub-title>
                          </v-list-tile-content>
                        </v-list-tile>

                        <v-list-tile>
                          <v-list-tile-action>
                            <v-btn flat fab small color="accent" @click="removeVendorMapping(vendor)">
                              <v-icon>cancel</v-icon>
                            </v-btn>
                          </v-list-tile-action>
                          <v-list-tile-content>
                            <v-list-tile-title>Forget this Quickbooks Vendor</v-list-tile-title>
                            <v-list-tile-sub-title class="font-italic">does not modify your vendor in Quickbooks at all</v-list-tile-sub-title>
                          </v-list-tile-content>
                        </v-list-tile>
                        <v-list-tile v-for="(discount, idx) in vendor.vendorDiscounts"
                                     :key="`disc_${idx}`">
                          <v-list-tile-action>
                            <v-btn flat fab small color="accent"
                                   @click="removeVendorDiscountMapping(vendor, discount)">
                              <v-icon>clear</v-icon>
                            </v-btn>
                          </v-list-tile-action>
                          <v-list-tile-content>
                            <v-list-tile-title>{{ discount.description }}</v-list-tile-title>
                            <v-list-tile-sub-title>
                              {{ vendorDiscountDescription(discount) }}
                              into account {{ vendorDiscountIncomeAccount(discount) }}
                            </v-list-tile-sub-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </v-list-group>
                    </template>
                    <v-subheader>Vendors without Discounts</v-subheader>
                    <v-list-tile v-if="loadingVendors">
                      <v-progress-circular
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                    </v-list-tile>
                    <template v-else
                              v-for="(vendor, i) in knownVendorsWithoutDiscounts">
                      <v-list-tile :key="'vendor-no-discounts' + i">
                        <v-list-tile-action>
                          <v-btn flat fab small color="accent" @click="removeVendorMapping(vendor)">
                            <v-icon>cancel</v-icon>
                          </v-btn>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>{{ vendor.ltnEmployeeName || vendor.ltnCompanyName }}</v-list-tile-title>
                          <v-list-tile-sub-title>{{ vendor.qbDisplayName }} ({{ vendor.qbCompanyId }})</v-list-tile-sub-title>
                          <v-list-tile-sub-title v-if="isDirectDispatched(vendor) && vendor.dispatcher">
                            Dispatcher {{ vendor.dispatcher.name}}
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                        <v-list-tile-avatar v-if="isDirectDispatched(vendor)">
                          <v-btn flat fab small color="accent" @click="showUpdateVendorDispatcherDialog(vendor)">
                            <v-icon>headset_mic</v-icon>
                          </v-btn>
                        </v-list-tile-avatar>
                      </v-list-tile>
                    </template>
                    <v-subheader>Other Vendors</v-subheader>
                    <v-list-group>
                      <v-list-tile slot="activator">
                        <v-list-tile-action>
                          <img class="align-center cursor-pointer"
                               height="32" width="32"
                               src="/static/Quickbooks-Logo.png"/>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>Quickbooks Vendors</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <template v-for="(vendor, i) in unknownVendors">
                        <v-list-tile :key="'unknown-vendor-' + i">
                          <v-list-tile-action />
                          <v-list-tile-content>
                            <v-list-tile-title>{{ vendor.qbDisplayName }}</v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </template>
                    </v-list-group>

                    <v-list-tile v-if="nonVendorDrivers.length == 0">
                      <v-list-tile-action>
                        <v-icon color="green">headset_mic</v-icon>
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title>Dispatched Drivers</v-list-tile-title>
                        <v-list-tile-sub-title>all directly dispatche drivers are known QB vendors</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-list-group v-if="nonVendorDrivers.length > 0">
                      <v-list-tile slot="activator">
                        <v-list-tile-action>
                          <v-icon color="green">headset_mic</v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>Dispatched Drivers</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <template v-for="(ee, i) in nonVendorDrivers">
                        <v-list-tile :key="'non-vendor-driver-' + i"
                                     v-if="ee.driverProfile">
                          <v-list-tile-action />
                          <v-list-tile-content>
                            <v-list-tile-title>{{ ee.userName }}</v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </template>
                    </v-list-group>
                  </v-list>

                </v-tab-item>
              </v-tabs-items>
            </v-tabs>
          </v-card>
        </v-flex>

        <v-flex xs12>
          <v-bottom-nav :active.sync="activeTab"

                        :value="true">
            <v-btn flat color="accent" value="connect">
              Connect
              <v-icon color="green">offline_bolt</v-icon>
            </v-btn>
            <v-btn flat color="accent"
                   value="defaults"
                   :disabled="!isConnected">
              Export Defaults
              <V-icon color="green">import_export</V-icon>
            </v-btn>
            <v-btn flat color="accent"
                   value="customers"
                   :disabled="!isConnected">
              Customers
              <v-icon color="green">face</v-icon>
            </v-btn>
            <v-btn flat color="accent"
                   value="qb_vendors"
                   :disabled="!isConnected">
              Vendors
              <v-icon color="green">share</v-icon>
            </v-btn>
          </v-bottom-nav>
        </v-flex>

      </v-layout>
    </v-container>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import firebase from 'firebase';
import API from '@/api';
import { mapGetters } from 'vuex';

import VQuickbooksConnectBtn from '@/components/qb/QuickbooksConnectButton';
import QuickbooksMixin from '@/mixins/quickbooksMixin';
import HelpPopover from '@/components/HelpPopover';

import { accountHasOfficePermission } from '@/store/permissions';

// https://developer.intuit.com/app/developer/qbo/docs/go-live/naming-and-logo-guidelines#button-graphics
export default {
  name: 'QuickbooksSection',
  mixins: [
    QuickbooksMixin,
  ],
  components: {
    HelpPopover,
    VQuickbooksConnectBtn,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 'customers',

      customerSearch: null,
      vendorSearch: null,

      showUpdateVendorDispatcher: false,
      vendorDispatcher: null,

      showExportDefaults: false,
      editExportDefaults: null,
      exportDefaultsStep: null,
      refDbKey: null,
      loadingBtn: null,
      loadingTaxCodes: false,

      qbButtonLoading: false,
    };
  },
  computed: {
    _() {
      return _;
    },
    configuredToCollectTax() {
      return this.quickbooksCompanyCollectsTax(this.company);
    },
    vendorDriversWithDiscounts() {
      // eslint-disable-next-line no-underscore-dangle
      const drivers = _.filter(this.vendorDrivers, vd => vd.__vendor && vd.__vendor.vendorDiscounts && vd.__vendor.vendorDiscounts.length > 0);
      return drivers;
    },
    vendorDriversWithoutDiscounts() {
      // eslint-disable-next-line no-underscore-dangle
      const drivers = _.reject(this.vendorDrivers, vd => vd.__vendor && vd.__vendor.vendorDiscounts && vd.__vendor.vendorDiscounts.length > 0);
      return drivers;
    },
    vendorDrivers() {
      return this.quickbooksVendorDrivers(_.get(this.company, 'employees', []), this.vendors);
    },
    nonVendorDrivers() {
      const term = this.vendorSearch ? this.vendorSearch.toLowerCase() : null;
      return _.chain(_.get(this.company, 'employees', []))
        .filter(ee => ee.driverProfile)
        .filter(d => !d.quickbooksVendorId)
        .filter(n =>
          !term ||
          _.get(n, 'contactEmail', '').toLowerCase().indexOf(term) >= 0 ||
          _.get(n, 'userName', '').toLowerCase().indexOf(term) >= 0)
        .value();
    },
    unknownVendors() {
      const term = this.vendorSearch ? this.vendorSearch.toLowerCase() : null;
      return _.chain(this.vendors)
        .reject(v => v.ltnCompanyId || v.ltnEmployeeId || v.defaultPaymentTermsId)
        .filter(v => this.quickbooksCompanyFilter(v, term))
        .value()
      ;
    },
    knownVendors() {
      const term = this.vendorSearch ? this.vendorSearch.toLowerCase() : null;
      return _.chain(this.vendors)
        .filter(v => v.ltnCompanyId || v.ltnEmployeeId || v.defaultPaymentTermsId != null)
        .filter(v => this.quickbooksCompanyFilter(v, term))
        .value()
      ;
    },
    knownVendorsWithDiscounts() {
      return _.filter(this.knownVendors, v => v.vendorDiscounts && v.vendorDiscounts.length > 0);
    },
    knownVendorsWithoutDiscounts() {
      return _.reject(this.knownVendors, v => v.vendorDiscounts && v.vendorDiscounts.length > 0);
    },
    knownCustomers() {
      const term = this.customerSearch ? this.customerSearch.toLowerCase() : null;
      return _.chain(this.customers)
        .filter(c => !!c.ltnCompanyId)
        .filter(c => this.quickbooksCompanyFilter(c, term))
        .value()
      ;
    },
    unknownCustomers() {
      const term = this.customerSearch ? this.customerSearch.toLowerCase() : null;
      return _.chain(this.customers)
        .reject(c => !!c.ltnCompanyId)
        .filter(c => this.quickbooksCompanyFilter(c, term))
        .value()
      ;
    },
    subscriptionSupportsBilling() {
      return this.quickbooksSubscriptionSupportsBilling(this.company);
    },

    isConnected() {
      return this.isQuickbooksConnected(this.company);
    },
    hasCurrentCredential() {
      return this.isQuickbooksCredentialsValid(this.company);
    },
    hasOfficePermission() {
      return this.isLtnAdmin || accountHasOfficePermission(this.userAccountCompany);
    },
    userAccountCompany() {
      return this.findCompanyAccount()(this.company.id);
    },
    salesItems() {
      return this.loadingSalesItems ? [] : this.quickbooksSalesItems(this.company.id);
    },
    loadingSalesItems() {
      return this.salesItemsStatus[this.company.id] || false;
    },
    salesTerms() {
      return this.loadingSalesTerms ? [] : this.quickbooksPaymentTerms(this.company.id);
    },
    loadingSalesTerms() {
      return this.salesTermsStatus[this.company.id] || false;
    },
    customers() {
      return this.loadingCustomers ? [] : this.quickbooksCustomers(this.company.id);
    },
    loadingCustomers() {
      return this.customersStatus[this.company.id] || false;
    },
    vendors() {
      return this.loadingVendors ? [] : this.quickbooksVendors(this.company.id);
    },
    loadingVendors() {
      return this.vendorsStatus[this.company.id] || false;
    },
    loadedAccounts() {
      return this.loadingAccounts ? [] : this.quickbooksAccounts(this.company.id);
    },
    loadingAccounts() {
      return this.accountsStatus[this.company.id] || false;
    },
    quickbooksExpenseAccounts() {
      return _.filter(this.loadedAccounts, a => a.classification === 'EXPENSE');
    },
    quickbooksIncomeAccounts() {
      return _.filter(this.loadedAccounts, a => a.classification === 'REVENUE');
    },
    defaultInvoiceSalesItem() {
      const item = {
        description: 'None Selected.',
      };

      return this.loadingSalesItems ? item : this.defaultCompanyQuickbooksInvoiceSalesItem(this.company) || item;
    },
    defaultCustomerInvoiceTerms() {
      const terms = {
        name: 'None Selected.',
      };

      return this.loadingSalesTerms ? terms : this.defaultCompanyQuickbooksCustomerInvoiceTerms(this.company) || terms;
    },
    defaultTaxCodes() {
      return this.defaultCompanyQuickbooksTaxCodes(this.company);
    },
    defaultVendorBillTerms() {
      const terms = {
        name: 'None Selected.',
      };

      return this.loadingSalesTerms ? terms : this.defaultCompanyQuickbooksVendorBillTerms(this.company) || terms;
    },
    defaultTransportationFeeExpenseAccount() {
      const account = {
        accountName: 'None Selected.',
      };

      return this.loadingAccounts ? account : this.defaultCompanyQuickbooksTransportationFeeExpenseAccount(this.company) || account;
    },
    defaultDispatchFeeIncomeAccount() {
      const account = {
        accountName: 'None Selected.',
      };

      return this.loadingAccounts ? account : this.defaultCompanyQuickbooksDispatchFeeIncomeAccount(this.company) || account;
    },
    defaultTrailerLeaseFeeIncomeAccount() {
      const account = {
        accountName: 'None Selected.',
      };

      return this.loadingAccounts ? account : this.defaultCompanyQuickbooksTrailerLeaseFeeIncomeAccount(this.company) || account;
    },
  },
  methods: {
    ...mapGetters('profiles', {
      findCompanyAccount: 'findCompanyAccount',
    }),
    completeStep2() {
      if (this.configuredToCollectTax) {
        this.exportDefaultsStep = 3;
      } else {
        this.saveExportDefaults();
      }
    },
    async connectAccount() {
      this.qbButtonLoading = true;
    },
    connectCompleted() {
      this.showExportDefaultsDialog()
      this.qbButtonLoading = false;
    },
    refreshCustomers() {
      this.refreshQuickbooksCustomers(this.company.id);
    },
    refreshVendors() {
      this.refreshQuickbooksVendors(this.company.id);
    },
    isDirectDispatched(qbCompany) {
      return !(qbCompany.ltnCompanyId || qbCompany.ltnEmployeeId)
           && qbCompany.defaultPaymentTermsId != null;
    },
    onDbUpdate(snapshot) {
      if (snapshot.val().refresh) {
        this.qbButtonLoading = false;

        this.reloadCompanyQuickbooksData();
        this.requestUpdateCompany();
        this.cleanDbRef();
      }
    },
    onCompanyUpdate() {

    },
    cleanDbRef() {
      if (this.refDbKey) {
        this.refDbKey.off('value', this.onDbUpdate);
        const dbRef = firebase.database().ref(`message_list/${this.company.id}`);
        dbRef.remove();
        this.refDbKey = null;
      }
    },
    async requestUpdateCompany() {
      await this.$store.dispatch('companies/getProfileCompany', { id: this.company.id });
      await this.$store.dispatch('companies/getCompany', { id: this.company.id });
    },
    async disconnectAccount() {
      // Open oath page, close it on redirectUri
      this.qbButtonLoading = true;
      const confirmText = `Are you sure you want to disconnect Quickbooks from ${this.$t('site-title.brief')}? This will delete all customer and vendor mappings, as well as default vendor discounts`;
      const confirm = await this.$confirm(confirmText);
      if (confirm) {
        await API.quickBooks.revokeAuthentication({ companyId: this.company.id });
        this.requestUpdateCompany();
        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Quickbooks no longer connected... reloading profile',
        });
      }
      this.qbButtonLoading = false;
    },
    quickbooksCompanyFilter(company, term) {
      if (!term) {
        return true;
      }

      console.log(company);

      return _.get(company, 'ltnEmployeeId', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'ltnCompanyId', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'ltnCompanyName', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'ltnEmployeeName', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'qbCompanyId', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'qbCompanyName', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'qbDisplayName', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'dispatcher.name', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'dispatcher.email', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'dispatcher.phone', '').toLowerCase().indexOf(term) >= 0 ||
        _.get(company, 'qbName', '').toLowerCase().indexOf(term) >= 0;
    },
    async removeCustomerMapping(customer) {
      const confirm = await this.$confirm(`Are you sure you want to remove the customer association for ${customer.ltnCompanyName}?`);

      if (confirm) {
        await API.quickBooks.deleteCustomerMapping({
          companyId: this.company.id,
          qbCustomerId: customer.qbCompanyId,
        });

        this.refreshQuickbooksCustomers(this.company.id);
        this.$store.dispatch('applications/setSuccessNotification', {
          message: `Customer no longer associated in your ${this.$t('site-title.brief')} Account`,
        });
      }
    },
    async removeVendorMapping(vendor) {
      const confirm = await this.$confirm(`Are you sure you want to remove the vendor association for ${vendor.qbDisplayName}?`);

      if (confirm) {
        await API.quickBooks.deleteVendorMapping({
          companyId: this.company.id,
          qbVendorId: vendor.qbCompanyId,
        });

        this.refreshQuickbooksVendors(this.company.id);
        this.requestUpdateCompany();
        this.$store.dispatch('applications/setSuccessNotification', {
          message: `Vendor no longer associated in your ${this.$t('site-title.brief')} Account`,
        });
      }
    },
    async removeVendorDiscountMapping(vendor, discount) {
      const confirm = await this.$confirm('Are you sure you want to remove discount ' + discount.description + ' for ' + vendor.qbDisplayName + '?');

      if (!confirm) {
        return;
      }

      await API.quickBooks.deleteVendorDiscountMapping({
        companyId: this.company.id,
        qbVendorId: vendor.qbCompanyId,
        discountId: discount.id,
      });

      this.refreshQuickbooksVendors(this.company.id);
      this.requestUpdateCompany();
      this.$store.dispatch('applications/setSuccessNotification', {
        message: 'Vendor discount removed',
      });
    },
    async showExportDefaultsDialog() {
      this.exportDefaultsStep = 1;

      this.editExportDefaults = {
        invoices: {
          salesItem: this.defaultInvoiceSalesItem.itemId ? this.defaultInvoiceSalesItem : null,
          paymentTerms: this.defaultCustomerInvoiceTerms.id ? this.defaultCustomerInvoiceTerms : null,
          taxCode: this.defaultTaxCodes.sales,
        },
        bills: {
          expenseAccount: this.defaultTransportationFeeExpenseAccount.accountId ? this.defaultTransportationFeeExpenseAccount : null,
          paymentTerms: this.defaultVendorBillTerms.id ? this.defaultVendorBillTerms : null,
          dispatchFeeAccount: this.defaultDispatchFeeIncomeAccount.accountId ? this.defaultDispatchFeeIncomeAccount : null,
          trailerLeaseFeeAccount: this.defaultTrailerLeaseFeeIncomeAccount.accountId ? this.defaultTrailerLeaseFeeIncomeAccount : null,
          taxCode: this.defaultTaxCodes.purchase,
        },
      };

      this.showExportDefaults = true;
    },
    async refreshQuickbooksAccountInfo() {
      try {
        // should we make this a mutation?
        const refreshed = await API.quickBooks.refreshQuickbooksAccount(this.company.id);

        this.requestUpdateCompany();
        this.reloadCompanyQuickbooksData();
      } catch (error) {
      this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      }
    },
    async saveExportDefaults() {
      const companyId = this.company.id;
      const body = {
        invoices: {
          salesItemId: _.get(this.editExportDefaults, 'invoices.salesItem.itemId'),
          paymentTermsId: _.get(this.editExportDefaults, 'invoices.paymentTerms.id'),

          taxCode: _.get(this.editExportDefaults, 'invoices.taxCode.id', null),
        },
        bills: {
          transportationExpenseAccountId: _.get(this.editExportDefaults, 'bills.expenseAccount.accountId'),
          paymentTermsId: _.get(this.editExportDefaults, 'bills.paymentTerms.id'),

          dispatchFeeIncomeAccountId: _.get(this.editExportDefaults, 'bills.dispatchFeeAccount.accountId'),
          trailerLeaseFeeIncomeAccountId: _.get(this.editExportDefaults, 'bills.trailerLeaseFeeAccount.accountId'),

          taxCode: _.get(this.editExportDefaults, 'bills.taxCode.id', null),
        },
      };

      try {
        this.loadingBtn = true;
        await API.quickBooks.saveExportDefaults(companyId, body);
        this.requestUpdateCompany();

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Quickbooks Exports Defaults saved',
        });

        this.showExportDefaults = false;
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingBtn = false;
      }
    },
    reloadCompanyQuickbooksData() {
      this.loadCompanyQuickbooksInfo(this.company.id);
    },

    closeUpdateVendorDispatcherDialog() {
      this.vendorDispatcher = null;
      this.showUpdateVendorDispatcher = false;
    },
    async showUpdateVendorDispatcherDialog(vendor) {
      console.log('Where is my vendor ID?', vendor);
      this.vendorDispatcher = {
        companyName: vendor.qbDisplayName,
        companyId: vendor.qbCompanyId,
        ..._.get(vendor, 'dispatcher', {}),
      };
      this.showUpdateVendorDispatcher = true;
    },
    async saveVendorDispatcher() {
      const body = { ...this.vendorDispatcher };

      try {
        this.loadingBtn = true;
        await API.quickBooks.updateVendorDispatcher(this.company.id, this.vendorDispatcher.companyId, body);
        this.refreshVendors();

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Dispatcher Contact info saved',
        });

        this.closeUpdateVendorDispatcherDialog();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingBtn = false;
      }
    },

    vendorSalesTerms(vendor) {
      const id = vendor.defaultPaymentTermsId;
      return _.find(this.salesTerms, t => t.id === id) || {};
    },
    vendorDiscountIncomeAccount(discount) {
      const account = _.find(this.loadedAccounts, a => a.accountId === discount.accountId) || {};
      return `${account.accountName}`;
    },
    vendorDiscountDescription(discount) {
      if (discount.amount != null) {
        return `$${discount.amount}`;
      }
      return `${discount.percent}%`;
    },
  },
  watch: {
    company(newVal) {
      this.onCompanyUpdate();
    }
  },
  destroyed() {
    this.cleanDbRef();
  },
};
</script>

<style scoped>
  .full-card-desktop {
    height: calc(100vh - 260px);
    overflow: auto;
  }

  .tall-title {
    height: 34px;
  }
</style>

<style>
  .qb_tabs .v-tabs__wrapper {
    display: none;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Companies/Sections/QuickbooksSection.vue